import {
  ArrowLeft,
  File,
  Zap,
  Upload,
  History,
  BookmarkCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DataHistory from "../data-history/DataHistory";
import { Tabs } from "@shared/ui/tabs";
import SavedTemplates from "../data-saved/SavedTemplates";
import { useMemo } from "react";
import { Info } from "lucide-react";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";

const FEATURES = [
  {
    icon: File,
    title: "Update Existing Data",
    description: "Modify and update your existing records in the system",
    iconColor: "bg-blue-100 text-blue-600",
    navigateTo: "/onboarding/data-migration/data-selection",
    example: "Example: Update meter status for existing consumer accounts.",
    exampleColor: "text-purple-500",
    operation_type: 1,
  },
  {
    icon: Zap,
    title: "Upload New Data",
    description: "Import new data files into the system",
    example:
      "Example: Import a list of new consumer connections with all required details.",
    exampleColor: "text-blue-500",
    iconColor: "bg-purple-100 text-purple-600",
    navigateTo: "/onboarding/data-migration/data-selection",
    operation_type: 2,
  },
];

const FeatureCard = ({
  icon: Icon,
  title,
  description,
  example,
  iconColor,
  exampleColor,
  onClick,
}) => (
  <div
    onClick={onClick}
    className="cursor-pointer bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
  >
    <div className="text-center">
      <div className={`inline-flex p-3 rounded-full ${iconColor} mb-4`}>
        <Icon size={24} />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      {example && (
        <p className={`text-sm italic mt-4 ${exampleColor}`}>{example}</p>
      )}
    </div>
  </div>
);

const DataUploadInterface = () => {
  const navigate = useNavigate();

  // Memoize tabComponents to prevent recreation on every render
  const tabComponents = useMemo(
    () => ({
      upload: {
        label: "Upload Data",
        icon: <Upload className="w-4 h-4" />,
        component: (
          <div className="space-y-8" key="upload-content">
            {/* Info Box Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                <Info size={20} className="text-blue-500" /> When to use which
                option?
              </h2>
              <div className="text-sm text-gray-700 space-y-3">
                <div>
                  <p className="font-semibold text-blue-700">
                    Upload New Data:
                  </p>
                  <p className="text-blue-600">
                    Use this option when you want to bring fresh data into the
                    system. For example, you can upload Consumers, Meters,
                    Assets, Tariffs, and more. On the next screen, you will see
                    cards for each data type. Each card clearly lists the{" "}
                    <span className="font-semibold">mandatory fields</span>{" "}
                    required for migration so you can prepare your files
                    correctly in advance.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-blue-700">
                    Update Existing Data:
                  </p>
                  <p className="text-blue-600">
                    Use this option when you want to modify or correct records
                    that already exist in the system. For example, updating
                    meter status, changing consumer contact details, or
                    adjusting tariff mappings. Before uploading, make sure your
                    data includes the unique identifiers (such as Consumer IDs
                    or Meter IDs) for the records you wish to update.
                  </p>
                </div>
              </div>
            </div>

            {/* Heading Section */}
            <div>
              <PageHeader
                title="What would you like to do?"
                subtitle="Choose whether you want to update existing data or upload new data"
              />
            </div>

            {/* Feature Cards Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {FEATURES.map((feature, index) => (
                <FeatureCard
                  key={index}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  example={feature.example}
                  iconColor={feature.iconColor}
                  exampleColor={feature.exampleColor}
                  onClick={() =>
                    navigate(feature.navigateTo, {
                      state: { operation_type: feature.operation_type },
                    })
                  }
                />
              ))}
            </div>
          </div>
        ),
        className: "flex-1",
      },

      history: {
        label: "Migration History",
        icon: <History className="w-4 h-4" />,
        component: <DataHistory key="data-history" />,
        className: "flex-1",
      },
      templates: {
        label: "Saved Templates",
        icon: <BookmarkCheck className="w-4 h-4" />,
        component: <SavedTemplates key="saved-templates" />,
        className: "flex-1",
      },
    }),
    [navigate]
  );

  return (
    <div className="min-h-screen w-full">
      <main className="flex flex-col gap-5">
        {/* Title Section */}
        <div>
          <PageHeader
            title="AI-Powered Data Upload"
            subtitle="Upload your data and let AI help you integrate it seamlessly into your system."
          />
        </div>

        <Tabs
          defaultValue="upload"
          className="mt-4 "
          tabsListClassName="grid gris-cols-2 flex"
          tabComponents={tabComponents}
        />
      </main>
      <Button className="mt-2" variant="outline" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </Button>
    </div>
  );
};

export default DataUploadInterface;
